import type { Issue } from './issuesApi'
import type { AdminTask } from './adminApi'
import type { Headline } from './headlinesApi'
import type { TaskPrefill } from '../components/AdminTasks'
import type { IssuePrefill } from '../components/Issues'

/**
 * The Shop L10 agenda, as Scott runs it in Ninety (verified against his live
 * account, 2026-10-08): six sections, 60 minutes. The database stores only a
 * section INDEX (0–5) and when that section started; the labels and minutes
 * live here, so changing the agenda is a change to this file and nothing
 * else. Order matters — the index is the position in this array.
 *
 * Headlines run second, straight after Segue and before the Scorecard (changed
 * 2026-10-21 at Scott's request). The stored index is what carries over, so a
 * meeting that is already open when this ships would jump to a different
 * section — don't deploy while a meeting is running.
 */
export type SectionKey = 'segue' | 'scorecard' | 'todos' | 'headlines' | 'ids' | 'conclude'

export interface AgendaItem {
  key: SectionKey
  label: string
  minutes: number
}

export const AGENDA: readonly AgendaItem[] = [
  { key: 'segue', label: 'Segue', minutes: 5 },
  { key: 'headlines', label: 'Headlines', minutes: 5 },
  { key: 'scorecard', label: 'Scorecard', minutes: 5 },
  { key: 'todos', label: 'To-Dos', minutes: 5 },
  { key: 'ids', label: 'IDS', minutes: 35 },
  { key: 'conclude', label: 'Conclude', minutes: 5 },
]

export const TOTAL_MINUTES = AGENDA.reduce((sum, s) => sum + s.minutes, 0)

/** The agenda item for a stored section index. Out-of-range falls back to Segue. */
export function agendaItem(section: number): AgendaItem {
  return AGENDA[section] ?? AGENDA[0]!
}

/**
 * Milliseconds left in the current section, negative once it has run over.
 *
 * `nowMs` must already be SERVER time — the caller subtracts its measured
 * clock skew first — so every phone counts down from the same instant the
 * presenter's does, whatever their own clocks say.
 */
export function sectionRemainingMs(
  meeting: { current_section: number; section_started_at: string },
  nowMs: number,
): number {
  const budget = agendaItem(meeting.current_section).minutes * 60_000
  return budget - (nowMs - Date.parse(meeting.section_started_at))
}

/** Milliseconds since the meeting started. Same server-time rule as above. */
export function elapsedMs(meeting: { started_at: string }, nowMs: number): number {
  return nowMs - Date.parse(meeting.started_at)
}

/** "4:05", or "-1:30" once over. Whole seconds; never shows "60" seconds. */
export function formatClock(ms: number): string {
  const sign = ms < 0 ? '-' : ''
  const total = Math.floor(Math.abs(ms) / 1000)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${sign}${m}:${String(s).padStart(2, '0')}`
}

/**
 * "Create a task" on an issue — the one builder for the Issues → Tasks handoff,
 * shared by App (the Tasks tab) and Meeting (the in-meeting detour), so the
 * two cannot drift on what a task made from an issue looks like.
 *
 * Carries the issue's own words across as the task notes, because whoever
 * picks the work up needs the detail, not just the headline.
 */
export function taskPrefillFromIssue(issue: Issue): TaskPrefill {
  const detail = issue.description.trim()
  return {
    issueId: issue.id,
    headlineId: null,
    title: issue.title,
    notes:
      `Reported by ${issue.reported_by_name}.` +
      (detail && detail !== issue.title.trim() ? `\n\n${detail}` : ''),
  }
}

/** All "Create an issue" needs from a to-do — an admin's AdminTask or a staff TaskRow. */
export type TaskRef = Pick<AdminTask, 'task_id' | 'title'>

/**
 * "Create an issue" on a to-do — the Ninety "linked issue". Someone hit a
 * problem with a to-do, or has something to bring up from doing it; this opens
 * the issue form linked to that to-do, so the issue is waiting in IDS and says
 * which to-do it came from. Admins and anyone assigned may do it (0019).
 *
 * The description starts EMPTY. It used to start with the to-do's title and
 * brief, but the first line becomes the issue's headline, so every issue raised
 * from one to-do ("Write down any concerns or process tweaks") was headlined
 * with the to-do's title instead of the actual concern. The form names the
 * to-do instead, and the issue card shows the link.
 */
export function issuePrefillFromTask(task: TaskRef): IssuePrefill {
  return {
    headlineId: null,
    taskId: task.task_id,
    taskTitle: task.title,
    description: '',
  }
}

/**
 * "Create a task" on a headline. The headline's words become the task title —
 * trimmed to a title's length, with the full text kept in the notes — and the
 * link is carried through so the headline is marked shared, as when one
 * becomes an issue.
 */
export function taskPrefillFromHeadline(
  headline: Pick<Headline, 'id' | 'body' | 'created_by_name'>,
): TaskPrefill {
  const body = headline.body.trim()
  const long = body.length > 120
  return {
    issueId: null,
    headlineId: headline.id,
    title: long ? `${body.slice(0, 117).trimEnd()}…` : body,
    notes: `From a headline by ${headline.created_by_name}.` + (long ? `\n\n${body}` : ''),
  }
}
